import {
  getRequestPAdmin,
  patchRequestPAdmin,
  postRequestPAdmin,
} from "common/HttpClient/http-utility";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import { fetchAppActiveCustomers } from "common/Services/ActiveCustomersMaster";
import { fetchAppCustomerRolesMaster } from "common/Services/CustomerRolesMaster";
import {
  CUSTOMER_ACCESS_STATUS,
  normalizeCustomerAccessStatus,
} from "common/Constants/AccessStatus.constants";
import {
  isCustomerAccessRowChanged,
  normalizePlatformUsername,
  PLATFORM_USER_ACCESS_UI,
} from "./PlatformUserAccess.constants";

const PLATFORM_MUTATION_SUCCESS_STATUSES = new Set([200, 201, 204]);

const resolvePhoneFromApi = (source) => {
  const value = source?.phoneNumber ?? source?.phone;
  const trimmed = String(value ?? "").trim();
  return trimmed;
};

export const isPlatformMutationSuccess = (response) => {
  if (response?.error != null && response.error !== "") {
    return false;
  }
  const status = Number(response?.status);
  if (
    Number.isFinite(status) &&
    !PLATFORM_MUTATION_SUCCESS_STATUSES.has(status)
  ) {
    return false;
  }
  return true;
};

const toPlatformMutationError = (
  response,
  fallback = "Please try again after some time"
) => ({
  status: response?.status,
  error:
    typeof response?.error === "string" && String(response.error).trim()
      ? response.error
      : fallback,
});

const deepClone = (value) => JSON.parse(JSON.stringify(value));

const usersExistsPath = INFRAMARK_CONSTANTS.users.services.usersExists;
const platformUsersPath = INFRAMARK_CONSTANTS.users.services.platformUsers;
const userInvitesPath = INFRAMARK_CONSTANTS.users.services.userInvites;

const extractEnvelope = (data) => {
  if (data == null) {
    return null;
  }
  if (typeof data === "object" && "response" in data && data.response !== undefined) {
    return data.response;
  }
  return data;
};

const mapCustomerBlockToAccess = (c) => {
  const accessStatus =
    normalizeCustomerAccessStatus(c.accessStatus ?? c.status) ||
    CUSTOMER_ACCESS_STATUS.ACTIVE;
  const customerCode = c.customerCode;
  return {
    accessId: c.accessId,
    customerId: c.customerId,
    customerName: c.customerName ?? "",
    customerCode,
    roles: c.roles ?? [],
    status: accessStatus,
    accessStatus,
  };
};

const normalizeRoleStrings = (roles) =>
  (roles ?? [])
    .map((role) => (typeof role === "string" ? role : role?.value || role?.label))
    .filter(Boolean);

const buildCustomerAccessItem = (row, originalAccess = []) => {
  const original = (originalAccess ?? []).find(
    (item) => String(item.customerId) === String(row.customerId)
  );
  const item = {
    customerId: row.customerId,
    customerCode: row.customerCode,
    customerName: row.customerName,
    accessStatus:
      normalizeCustomerAccessStatus(row.accessStatus ?? row.status) ||
      CUSTOMER_ACCESS_STATUS.ACTIVE,
    roles: normalizeRoleStrings(row.roles),
  };
  const accessId = row.accessId ?? original?.accessId;
  if (accessId != null) {
    item.accessId = accessId;
  }
  return item;
};

const buildCustomerAccessMutationBody = (userId, customerAccessItems) => {
  const numericUserId = Number(userId);
  return {
    userId: Number.isFinite(numericUserId) ? numericUserId : userId,
    customerAccess: customerAccessItems,
  };
};

export const buildCustomerAccessPatchBody = (userId, originalAccess, currentRows) => {
  const customerAccess = (currentRows ?? [])
    .filter((row) => row?.customerId != null && String(row.customerId).trim() !== "")
    .filter((row) => {
      const original = (originalAccess ?? []).find(
        (item) => String(item.customerId) === String(row.customerId)
      );
      return isCustomerAccessRowChanged(original, row);
    })
    .map((row) => buildCustomerAccessItem(row, originalAccess));
  return buildCustomerAccessMutationBody(userId, customerAccess);
};

const mapLatestInvite = (latestInvite) => {
  if (!latestInvite || typeof latestInvite !== "object") {
    return null;
  }
  return {
    inviteId: latestInvite.inviteId,
    userId: latestInvite.userId,
    email: latestInvite.email ?? "",
    purpose: latestInvite.purpose,
    inviteStatus: latestInvite.inviteStatus,
    expiresAt: latestInvite.expiresAt,
    consumedAt: latestInvite.consumedAt,
    revokedAt: latestInvite.revokedAt,
    revokeReason: latestInvite.revokeReason,
    createdAt: latestInvite.createdAt,
  };
};

const mapApiUserRecord = (item) => ({
  id: item.id,
  username: normalizePlatformUsername(item.username),
  email: item.email ?? "",
  firstName: String(item.firstName ?? item.first_name ?? "").trim(),
  lastName: String(item.lastName ?? item.last_name ?? "").trim(),
  phone: resolvePhoneFromApi(item),
  platformRole: item.platformRole ?? PLATFORM_USER_ACCESS_UI.defaultPlatformRole,
  status: item.accountStatus ?? item.status ?? CUSTOMER_ACCESS_STATUS.ACTIVE,
  customerCount: item.customerCount,
  lastActiveCustomerCode: item.lastActiveCustomerCode,
  latestInvite: mapLatestInvite(item.latestInvite),
  customerAccess: (item.customers ?? item.customerAccess ?? []).map(
    mapCustomerBlockToAccess
  ),
});

const toListRow = (user) => ({
  id: user.id,
  username: user.username,
  email: user.email,
  firstName: user.firstName,
  lastName: user.lastName,
  phone: user.phone,
  platformRole: user.platformRole,
  status: user.status,
  inviteId: user.latestInvite?.inviteId ?? null,
});

const mapApiPaginationToState = (apiPagination, requestedPage, requestedSize) => {
  const p = apiPagination ?? {};
  const size = Number(p.pageSize ?? p.size ?? requestedSize ?? 20) || 20;
  const totalCount = Number(p.totalCount ?? 0);
  let page;
  if (p.pageIndex != null) {
    page = Number(p.pageIndex);
  } else if (p.page != null) {
    const apiPage = Number(p.page);
    page = apiPage > 0 ? apiPage - 1 : 0;
  } else {
    page = Math.max(0, Number(requestedPage) || 0);
  }
  return {
    page,
    size,
    totalCount,
    totalPages: Number(p.totalPages ?? (totalCount === 0 ? 0 : Math.ceil(totalCount / size))),
    hasNext: Boolean(p.hasNext),
    hasPrevious: Boolean(p.hasPrevious),
  };
};

const normalizeRolesArray = (roles) =>
  (roles ?? [])
    .map((r) => (typeof r === "string" ? r : r?.value || r?.label))
    .filter(Boolean);

const mapUserInviteRequestBody = (payload) => {
  const customerAccess = (payload?.customerAccess ?? [])
    .filter((a) => a?.customerId != null && String(a.customerId).trim() !== "")
    .map((a) => ({
      customerId: a.customerId,
      customerCode: a.customerCode,
      roles: normalizeRolesArray(a.roles),
      accessStatus:
        normalizeCustomerAccessStatus(a.accessStatus) ||
        CUSTOMER_ACCESS_STATUS.INVITED,
    }));
  return {
    email: String(payload?.email ?? "").trim(),
    platformRole:
      payload?.platformRole || PLATFORM_USER_ACCESS_UI.defaultPlatformRole,
    customerAccess,
    sendInviteEmail: payload?.sendInviteEmail !== false,
  };
};

const mapInviteResponseToUserRecord = (responseData, payload) => {
  const body = extractEnvelope(responseData) ?? responseData ?? {};
  const email = String(payload?.email ?? "").trim();
  const id = body.id ?? `u-${Date.now()}`;
  const username = normalizePlatformUsername(body.username);
  const platformRole =
    body.platformRole ??
    payload?.platformRole ??
    PLATFORM_USER_ACCESS_UI.defaultPlatformRole;
  const status =
    body.accountStatus ?? body.status ?? CUSTOMER_ACCESS_STATUS.INVITED;
  const customerAccessFromBody = Array.isArray(body.customers)
    ? body.customers
    : Array.isArray(body.customerAccess)
      ? body.customerAccess
      : null;
  const sourceAccess = customerAccessFromBody ?? payload?.customerAccess ?? [];
  const customerAccess = (sourceAccess ?? []).map((access) => {
    const accessStatus =
      normalizeCustomerAccessStatus(
        access.accessStatus ?? access.status
      ) || CUSTOMER_ACCESS_STATUS.INVITED;
    return {
      customerId: access.customerId,
      customerName: access.customerName,
      customerCode: access.customerCode,
      roles: normalizeRolesArray(access.roles),
      status: accessStatus,
      accessStatus,
    };
  });
  return {
    id,
    username,
    email: body.email ?? email,
    phone: resolvePhoneFromApi(body),
    platformRole,
    status,
    latestInvite: mapLatestInvite(body.latestInvite),
    customerAccess,
  };
};

const userAccessById = new Map();

let customerDirectory = [];

const ensureCustomerDirectory = async () => {
  if (Array.isArray(customerDirectory) && customerDirectory.length > 0) {
    return customerDirectory;
  }
  customerDirectory = (await fetchAppActiveCustomers()) ?? [];
  return customerDirectory;
};

const extractUsersListPayload = (responseData) => {
  if (responseData?.success === true && Array.isArray(responseData.data)) {
    return responseData;
  }
  if (Array.isArray(responseData?.data)) {
    return { data: responseData.data, pagination: responseData.pagination };
  }
  return { data: [], pagination: {} };
};

const parseUsersListResponse = (body, requestedPage, requestedSize) => {
  const parsed = extractUsersListPayload(body);
  const rawList = parsed.data ?? [];
  const records = rawList.map(mapApiUserRecord);
  records.forEach((r) => {
    userAccessById.set(String(r.id), r);
  });
  const pagination = mapApiPaginationToState(
    parsed.pagination,
    requestedPage,
    requestedSize
  );
  return {
    list: records.map(toListRow),
    pagination,
    records,
  };
};

const buildUsersListUrl = ({
  page,
  size,
  sort,
  username,
  email,
  firstName,
  lastName,
  phone,
  platformRole,
  accountStatus,
}) => {
  const pageIndex = Math.max(0, Number(page) || 0);
  const pageSize = Math.max(1, Number(size) || 20);
  const params = new URLSearchParams();
  params.set("pageIndex", String(pageIndex));
  params.set("pageSize", String(pageSize));
  const sortParam = String(sort ?? "").trim();
  if (sortParam) {
    params.set("sort", sortParam);
  }
  const setIf = (key, value) => {
    const trimmed = String(value ?? "").trim();
    if (trimmed) {
      params.set(key, trimmed);
    }
  };
  setIf("username", username);
  setIf("email", email);
  setIf("firstName", firstName);
  setIf("lastName", lastName);
  setIf("phoneNumber", phone);
  setIf("platformRole", platformRole);
  setIf("accountStatus", accountStatus);
  return `${platformUsersPath}?${params.toString()}`;
};

export const PlatformUserAccessServices = () => {
  const getCustomers = async () => {
    const normalized = await fetchAppActiveCustomers();
    customerDirectory = normalized;
    return { data: normalized };
  };

  const getAppCustomerRoles = async () => ({
    data: await fetchAppCustomerRolesMaster(),
  });

  const checkEmailExists = async (email) => {
    const trimmed = String(email ?? "").trim();
    if (!trimmed) {
      return { data: { exists: false } };
    }
    const url = `${usersExistsPath}?email=${encodeURIComponent(trimmed)}`;
    const response = await getRequestPAdmin(url);
    if (response?.error) {
      return { error: response.error || "Unable to verify email." };
    }
    const payload = extractEnvelope(response?.data) ?? response?.data;
    return { data: { exists: Boolean(payload?.exists) } };
  };

  const getUsers = async ({
    page = 0,
    size = 20,
    sort,
    username = "",
    email = "",
    firstName = "",
    lastName = "",
    phone = "",
    platformRole = "",
    accountStatus = "",
  } = {}) => {
    const listFilters = {
      username: String(username ?? "").trim(),
      email: String(email ?? "").trim(),
      firstName: String(firstName ?? "").trim(),
      lastName: String(lastName ?? "").trim(),
      phone: String(phone ?? "").trim(),
      platformRole: String(platformRole ?? "").trim(),
      accountStatus: String(accountStatus ?? "").trim(),
    };
    const url = buildUsersListUrl({
      page,
      size,
      sort,
      ...listFilters,
    });
    const response = await getRequestPAdmin(url);
    if (response?.error) {
      return {
        error: response.error || "Unable to load user access list.",
      };
    }
    const parsed = parseUsersListResponse(response?.data, page, size);
    return {
      data: {
        list: parsed.list,
        pagination: parsed.pagination,
      },
    };
  };

  const getUserById = async (userId) => {
    await ensureCustomerDirectory();
    const key = String(userId);
    const fromMap = userAccessById.get(key);
    if (fromMap) {
      return { data: deepClone(fromMap) };
    }
    const url = `${platformUsersPath}/${key}`;
    const response = await getRequestPAdmin(url);
    if (response?.error) {
      return { error: response.error || "User not found" };
    }
    const body = extractEnvelope(response?.data) ?? response?.data;
    if (!body || typeof body !== "object") {
      return { error: "User not found" };
    }
    const record = mapApiUserRecord(body);
    userAccessById.set(key, record);
    return { data: deepClone(record) };
  };

  const resendUserInvite = async (inviteId) => {
    const key = String(inviteId ?? "").trim();
    if (!key) {
      return { error: "Invite id is required." };
    }
    const url = `${userInvitesPath}/${encodeURIComponent(key)}/resend`;
    const response = await postRequestPAdmin(url, {});
    if (!isPlatformMutationSuccess(response)) {
      return toPlatformMutationError(
        response,
        "Resend failed. Please try again."
      );
    }
    return { data: response?.data ?? {}, status: response?.status };
  };

  const inviteUser = async (payload) => {
    await ensureCustomerDirectory();
    const requestBody = mapUserInviteRequestBody(payload);
    const response = await postRequestPAdmin(userInvitesPath, requestBody);
    if (!isPlatformMutationSuccess(response)) {
      return toPlatformMutationError(
        response,
        "Invitation could not be sent. Please try again."
      );
    }
    const created = mapInviteResponseToUserRecord(response?.data, payload);
    userAccessById.set(String(created.id), created);
    return { data: deepClone(created), status: response?.status };
  };

  const patchPlatformUserAccountStatus = async (
    userId,
    { accountStatus, reason = "" } = {}
  ) => {
    const key = String(userId);
    const url = `${platformUsersPath}/${key}/accountStatus`;
    const body = {
      accountStatus: normalizeCustomerAccessStatus(accountStatus) || accountStatus,
      reason: reason == null ? "" : String(reason),
    };
    const response = await patchRequestPAdmin(url, body);
    if (!isPlatformMutationSuccess(response)) {
      return toPlatformMutationError(
        response,
        "User account status could not be updated. Please try again."
      );
    }
    const current = userAccessById.get(key);
    if (current) {
      userAccessById.set(key, {
        ...current,
        status: body.accountStatus,
      });
    }
    return { data: response?.data ?? body, status: response?.status };
  };

  const patchPlatformUser = async (userId, patchBody) => {
    const key = String(userId);
    const url = `${platformUsersPath}/${key}`;
    const response = await patchRequestPAdmin(url, patchBody);
    if (!isPlatformMutationSuccess(response)) {
      return toPlatformMutationError(
        response,
        "Changes could not be saved. Please try again."
      );
    }
    const current = userAccessById.get(key);
    if (current) {
      const updated = {
        ...current,
        username: patchBody.username ?? current.username,
        phone: patchBody.phoneNumber ?? current.phone,
        platformRole: patchBody.platformRole ?? current.platformRole,
        status: patchBody.accountStatus ?? current.status,
      };
      userAccessById.set(key, updated);
    }
    return { data: response?.data ?? patchBody, status: response?.status };
  };

  const patchPlatformUserCustomerAccess = async (
    userId,
    originalAccess,
    currentRows
  ) => {
    await ensureCustomerDirectory();
    const key = String(userId);
    const url = `${platformUsersPath}/${key}/customerAccess`;
    const patchBody = buildCustomerAccessPatchBody(key, originalAccess, currentRows);

    if (!patchBody.customerAccess?.length) {
      return { data: {}, status: 200 };
    }

    const response = await patchRequestPAdmin(url, patchBody);
    if (!isPlatformMutationSuccess(response)) {
      return toPlatformMutationError(
        response,
        "Changes could not be saved. Please try again."
      );
    }

    const refreshed = await getUserById(key);
    if (refreshed?.data) {
      userAccessById.set(key, refreshed.data);
      return { data: refreshed.data, status: response?.status };
    }

    return { data: response?.data ?? patchBody, status: response?.status };
  };

  return {
    getCustomers,
    getAppCustomerRoles,
    checkEmailExists,
    getUsers,
    getUserById,
    resendUserInvite,
    inviteUser,
    patchPlatformUser,
    patchPlatformUserAccountStatus,
    patchPlatformUserCustomerAccess,
    buildCustomerAccessPatchBody,
  };
};
